import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, FileText, Lock, Clock, Scale, UserCheck, Upload, AlertCircle } from 'lucide-react';

interface NdaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
}

export function NdaModal({ isOpen, onClose, onAgree }: NdaModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal Container - Centered */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-3xl my-auto"
            >
              <div className="glass rounded-2xl overflow-hidden flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-600/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">
                      Perjanjian Kerahasiaan (NDA)
                    </h2>
                    <p className="text-white/50 text-sm">
                      Non-Disclosure Agreement
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Section 1 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-primary-400" />
                    <h3 className="text-white font-semibold">1. Definisi Informasi Rahasia</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    "Informasi Rahasia" mencakup seluruh data, dokumen, rencana bisnis, proyeksi keuangan, 
                    strategi operasional, daftar klien, metodologi, dan informasi proprietari lainnya yang 
                    disampaikan oleh Pemilik Informasi (Anda) kepada Penerima (Emporium Solution) dalam rangka 
                    layanan Funding Advisory & Channeling.
                  </p>
                </div>
                
                {/* Section 2 */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Lock className="w-4 h-4 text-primary-400" />
                    <h3 className="text-white font-semibold">2. Kewajiban Kerahasiaan</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-2">
                    Emporium Solution berkewajiban untuk:
                  </p>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Menjaga kerahasiaan seluruh Informasi Rahasia yang diterima</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Tidak mengungkapkan, menyebarkan, atau membagikan informasi kepada pihak ketiga tanpa izin tertulis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Menggunakan informasi hanya untuk tujuan evaluasi dan matching pendanaan</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Menerapkan langkah-langkah keamanan yang memadai untuk melindungi informasi</span>
                    </li>
                  </ul>
                </div>
                
                {/* Section 3 - Client Obligations */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <UserCheck className="w-4 h-4 text-primary-400" />
                    <h3 className="text-white font-semibold">3. Pernyataan dan Kewajiban Klien</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-2">
                    Dengan mengunggah dokumen-dokumen melalui platform ini, Anda (Klien) menyatakan:
                  </p>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Anda menyetujui dan memahami sepenuhnya isi Perjanjian Kerahasiaan ini</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Anda mengunggah dokumen secara sukarela dan tanpa paksaan dari pihak manapun</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Anda memiliki hak dan kewenangan untuk membagikan informasi yang diunggah</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Informasi yang diunggah adalah akurat, lengkap, dan tidak menyesatkan</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Anda bertanggung jawab penuh atas kebenaran dan legalitas dokumen yang diunggah</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Anda memberikan izin kepada Emporium Solution untuk menggunakan informasi untuk evaluasi dan matching investor</span>
                    </li>
                  </ul>
                </div>

                {/* Section 4 - Document Upload Terms */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Upload className="w-4 h-4 text-primary-400" />
                    <h3 className="text-white font-semibold">4. Ketentuan Pengunggahan Dokumen</h3>
                  </div>
                  <ul className="space-y-2 text-white/70 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Seluruh dokumen yang diunggah akan diperlakukan sebagai Informasi Rahasia</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Dokumen hanya akan dibagikan kepada calon investor/pendana yang telah ditandatangani NDA</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Emporium Solution berhak menolak aplikasi yang tidak memenuhi persyaratan</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1">•</span>
                      <span>Anda dapat meminta penghapusan dokumen kapan saja dengan mengirimkan permintaan tertulis</span>
                    </li>
                  </ul>
                </div>

                {/* Section 5 - Consequences */}
                <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                    <h3 className="text-white font-semibold">5. Sanksi Pelanggaran</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Setiap pelanggaran terhadap ketentuan dalam Perjanjian ini oleh pihak manapun 
                    dapat dikenakan sanksi sesuai hukum yang berlaku di Indonesia, termasuk namun tidak 
                    terbatas pada ganti rugi, sanksi pidana, dan/atau tuntutan hukum lainnya.
                  </p>
                </div>

                {/* Section 6 - Duration */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-primary-400" />
                    <h3 className="text-white font-semibold">6. Jangka Waktu</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Kewajiban kerahasiaan ini berlaku selama <strong className="text-white">3 (tiga) tahun</strong> sejak 
                    tanggal penyerahan informasi, terhitung sejak dokumen diunggah melalui platform ini.
                  </p>
                </div>
                
                {/* Section 7 - Legal */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Scale className="w-4 h-4 text-primary-400" />
                    <h3 className="text-white font-semibold">7. Hukum yang Berlaku</h3>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Perjanjian ini tunduk pada dan diinterpretasikan sesuai dengan hukum Negara Republik Indonesia. 
                    Setiap perselisihan yang timbul dari perjanjian ini akan diselesaikan melalui musyawarah 
                    atau melalui pengadilan yang berwenang di Indonesia.
                  </p>
                </div>
                
                {/* Section 8 - Exceptions */}
                <div className="p-4 bg-primary-600/10 rounded-xl border border-primary-500/20">
                  <h3 className="text-white font-semibold mb-2">Pengecualian</h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    Kewajiban kerahasiaan tidak berlaku untuk informasi yang: (a) sudah menjadi domain publik, 
                    (b) diterima dari pihak ketiga yang berhak mengungkapkannya, (c) telah dikembangkan secara 
                    independen oleh Penerima, atau (d) diwajibkan untuk diungkapkan oleh hukum atau peraturan yang berlaku.
                  </p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={onAgree}
                  className="flex-1 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
                >
                  Saya Setuju
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
                >
                  Tutup
                </button>
              </div>
            </div>
          </motion.div>
        </div>
        </>
      )}
    </AnimatePresence>
  );
}
